import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuthStore, type UserRole } from "@/store/auth";

/**
 * Subscribes once (per session) to Postgres-change events on the five tables
 * that drive every counter, list, and KPI in the app. When a row changes
 * anywhere — another tab, another user on the team, an edge function — we
 * invalidate the React Query keys that depend on it so visible counts and
 * lists refresh without the user reloading.
 *
 * Why one channel: each `supabase.channel(...)` opens a websocket frame.
 * Bundling all five table subscriptions into one channel keeps the open
 * connection count at 1, which matters on Supabase's free tier (concurrent
 * realtime limit) and is faster to set up on slow connections.
 *
 * RLS still gates the stream — Supabase replays change rows only to
 * subscribers that pass the table's SELECT policy. So `activity_log` events
 * only reach admin tabs, `leads` events only reach team members, etc. This
 * means the hook is safe to run unconditionally for every signed-in user.
 *
 * Mount it once, in AppShell, behind the auth guard. Don't mount in pages —
 * navigating tears down the subscription which causes a small reconnect
 * thrash on every page change.
 */
export function useRealtimeSync(enabled: boolean) {
  const qc = useQueryClient();
  // Hold the channel + a "current invalidator" ref so we can update the
  // closure that fires from the channel callback without re-subscribing on
  // every render. queryClient is stable but we belt-and-braces here.
  const qcRef = useRef(qc);
  qcRef.current = qc;

  useEffect(() => {
    if (!enabled) return;

    // Channel name is per-session — Supabase requires uniqueness across the
    // socket connection. A random suffix prevents collisions if a stale
    // channel is still draining server-side after a hot-reload.
    const channelName = `app-sync-${Math.random().toString(36).slice(2, 10)}`;
    const channel = supabase.channel(channelName);

    // Helper: register a wildcard listener for a single table with the
    // invalidation set that change should trigger.
    function listen(table: string, invalidate: () => void) {
      channel.on(
        // postgres_changes payload is loosely typed in supabase-js — the
        // generic accepts any shape, we only need the event firing.
        "postgres_changes" as never,
        { event: "*", schema: "public", table } as never,
        () => invalidate(),
      );
    }

    listen("leads", () => {
      // Lead row changes shake every leads-derived surface: list pages,
      // mappable-pool (key ["leads", "mappable"]), planner candidates,
      // dashboard KPIs, route detail joins. Invalidating the top-level
      // ["leads"] key invalidates every useQuery whose key starts with
      // ["leads", ...] — including the mappable pool, so we don't need a
      // separate invalidate for it.
      qcRef.current.invalidateQueries({ queryKey: ["leads"] });
      qcRef.current.invalidateQueries({ queryKey: ["dashboard"] });
    });

    listen("routes", () => {
      qcRef.current.invalidateQueries({ queryKey: ["routes"] });
      qcRef.current.invalidateQueries({ queryKey: ["dashboard"] });
    });

    listen("route_stops", () => {
      qcRef.current.invalidateQueries({ queryKey: ["routes"] });
      qcRef.current.invalidateQueries({ queryKey: ["dashboard"] });
      // Mappable pool key is ["leads", "mappable"] (Plan, Map, RoutesMap) and
      // ["mappable-leads", "for-route", id] (Detail's Add picker). Invalidate
      // BOTH families because adding/removing a stop shifts the pool.
      qcRef.current.invalidateQueries({ queryKey: ["leads"] });
      qcRef.current.invalidateQueries({ queryKey: ["mappable-leads"] });
    });

    listen("lead_status_history", () => {
      // History rows are the truth source for the dashboard KPI counts.
      qcRef.current.invalidateQueries({ queryKey: ["dashboard"] });
    });

    listen("appointments", () => {
      // Dashboard widget + due-soon reminder hook both read
      // ["appointments", "open"]. Booked/updated rows from any tab or the
      // mobile app surface without a manual refresh.
      qcRef.current.invalidateQueries({ queryKey: ["appointments"] });
    });

    listen("activity_log", () => {
      qcRef.current.invalidateQueries({ queryKey: ["activity"] });
    });

    listen("teams", () => {
      // Org-level settings (name, working hours, timezone, description)
      // mutate the team row. Invalidating the settings query lets every
      // admin tab see edits live; the consumer pages (Profile, UserMenu)
      // pull from the same key.
      qcRef.current.invalidateQueries({ queryKey: ["team", "settings"] });
    });

    listen("depots", () => {
      // Default-depot flips, depot creation/deletion. Affects: Settings/
      // Organization (default badge), Plan (pre-selected depot), Leads/Map
      // and Routes/Map (depot pins on the leaflet). All keyed on ["depots"].
      qcRef.current.invalidateQueries({ queryKey: ["depots"] });
    });

    listen("zones", () => {
      // Zone CRUD (polygon, zip-set, circle, default_assignee changes).
      // Leads/Map overlays render from this key; without invalidation,
      // admins on other tabs see stale geometry until manual reload.
      // Realtime publication for `zones` lives in migration 0026.
      qcRef.current.invalidateQueries({ queryKey: ["zones"] });
    });

    // app_users: role changes + deactivation propagate here. Three effects:
    //   1. Always invalidate ["team", "members"] so the Team page table
    //      reflects the change for any admin who happens to be viewing it.
    //   2. If the changed row matches the SIGNED-IN user, synchronously
    //      patch the auth-store role from the realtime payload BEFORE
    //      kicking off the async rehydrate. This closes the round-trip
    //      window where AdminRoute / useIsAdmin would still read the
    //      stale role for the duration of the SELECT in rehydrateUser().
    //   3. If the row is gone (DELETE) or deactivated (is_active=false),
    //      sign the session out — there's no recoverable state.
    channel.on(
      "postgres_changes" as never,
      { event: "*", schema: "public", table: "app_users" } as never,
      (payload: {
        eventType?: string;
        new?: { id?: string; role?: UserRole; is_active?: boolean };
        old?: { id?: string };
      }) => {
        qcRef.current.invalidateQueries({ queryKey: ["team", "members"] });
        const store = useAuthStore.getState();
        const callerId = store.user?.id;
        const changedId = payload?.new?.id ?? payload?.old?.id;
        if (!callerId || !changedId || callerId !== changedId) return;

        // DELETE or deactivation → kill the session immediately.
        if (payload?.eventType === "DELETE" || payload?.new?.is_active === false) {
          void store.signOut();
          return;
        }

        // Synchronous role patch from payload — every zustand subscriber
        // (AdminRoute, useIsAdmin, sidebar gate) re-renders on the next
        // tick with the new role. No fetch in the critical path.
        const nextRole = payload?.new?.role;
        if (nextRole && store.user && nextRole !== store.user.role) {
          useAuthStore.setState({ user: { ...store.user, role: nextRole } });
        }
        // Defense-in-depth: refetch in case other fields (full_name,
        // hasMfa) changed in the same UPDATE.
        void store.rehydrateUser();
      },
    );

    channel.subscribe();

    return () => {
      // Async, fire-and-forget — Supabase tears the websocket down server-
      // side too. Ignored Promise is intentional.
      void supabase.removeChannel(channel);
    };
  }, [enabled]);
}
